import * as React from 'react'

import { Scope, useEventListener } from '../effects/EventListener'

/* TYPES */

interface EventConfig {
  event: string
  options?: AddEventListenerOptions
  scope?: Scope
}

interface EventApi {
  value: any
}

interface Props extends EventConfig {
  children?: React.ReactNode
}

interface RenderProps extends EventConfig {
  children: (props: EventApi) => {}
}

/* HOOK */

export const useEvent = ({
  event,
  options,
  scope = window,
}: EventConfig): EventApi => {
  const [value, handler] = React.useState(null)

  useEventListener({ event, handler, options, scope })

  return {
    value,
  }
}

/* DECORATOR */

export const withEvent = <T extends {}>(config: EventConfig) => (
  Component: React.ComponentType<T>
) => (props: T & { event: EventConfig }) => (
  <Component {...props} event={useEvent({ ...config, ...props.event })} />
)

/* RENDER PROP */

export const Event = ({ children, ...props }: RenderProps) => {
  const event = useEvent(props)

  return typeof children === 'function' ? children(event) : children
}

/* CONTEXT */

export const EventContext = React.createContext<EventApi>({
  value: null,
})

export const EventProvider = ({ children, ...props }: Props) => (
  <EventContext.Provider value={useEvent(props)}>
    {children}
  </EventContext.Provider>
)

export const consumeEvent = () => React.useContext(EventContext)
